import { ErrorHandler, inject } from '@angular/core';

import type { Channel, SocketConnectOption } from 'phoenix';
import { Socket } from 'phoenix';
import type { Observer } from 'rxjs';
import { BehaviorSubject, catchError, Observable, ReplaySubject, retry, timer } from 'rxjs';
import { distinctUntilChanged, mapTo } from 'rxjs/operators';

const WS_CLOSE_CODE_NORMAL = 1000;
const JOIN_RETRY_ATTEMPTS = 10;

export abstract class WebsocketService {
  public socket: Socket;
  public ready$: Observable<void>;

  protected errorHandler = inject(ErrorHandler);

  private isConnected$ = new ReplaySubject<Socket>(1);
  private socketParams$ = new BehaviorSubject<object>({});

  constructor() {
    this.ready$ = this.isConnected$.asObservable().pipe(distinctUntilChanged(), mapTo(undefined));
  }

  public updateParams(params: object): void {
    this.socketParams$.next(params);
  }

  public subscribeToChannel(topic: string) {
    return new Observable((observer: Observer<Channel>) => {
      const channel = this.socket.channel(topic);
      const channelPush = channel.join();

      channelPush.receive('ok', () => observer.next(channel));
      channelPush.receive('error', error => {
        observer.error(
          `Joining the ${topic} channel failed because of following reason: ${error.reason}`,
        );
      });
      channelPush.receive('timeout', () =>
        observer.error(`Joining the ${topic} channel failed because of timeout`),
      );

      return () => channel.leave();
    }).pipe(
      retry({
        count: JOIN_RETRY_ATTEMPTS,
        resetOnSuccess: true,
        delay: (err, retryCount) => {
          const NEXT_RETRY_ATTEMPT_MS = retryCount ** 2 * 1000;
          return timer(NEXT_RETRY_ATTEMPT_MS);
        },
      }),
      catchError(err => {
        this.errorHandler.handleError(`${err}`.replace(/:\d*/g, ':id'));
        throw err;
      }),
    );
  }

  protected socketConnect(url: string, options?: Partial<SocketConnectOption>): Socket {
    this.socketParams$.next(options?.params || {});
    this.socket = new Socket(url, {
      ...(options || {}),
      params: () => this.socketParams$.getValue(),
    });

    this.socket.onOpen(() => this.isConnected$.next(this.socket));
    this.socket.onClose((event: CloseEvent) => {
      if (event && WS_CLOSE_CODE_NORMAL === event.code) {
        /*
         * This is caused by heartbeat timeout which may occur after device
         * will wake up from sleep or hibernate or after network change
         */
        this.socket.disconnect(() => this.socket.connect());
      }
    });
    this.socket.connect();

    return this.socket;
  }
}
